/**
 * Sonner-backed toast shim — drop-in replacement for the old shadcn toast system.
 * Existing callers that do `toast({ description, variant })` keep working.
 * New code should import `toast` from "sonner" directly.
 */
import { toast as sonnerToast } from "sonner";

interface ToastOptions {
  title?: string;
  description?: string;
  variant?: "default" | "destructive" | "success" | "warning";
  duration?: number;
  /** If set, only one open toast with this key is shown at a time (deduplicated via sonner's `id`). */
  dedupeKey?: string;
}

function toast(opts: ToastOptions | string) {
  if (typeof opts === "string") {
    sonnerToast(opts);
    return;
  }

  const message = opts.title || opts.description || "";
  const body = opts.title && opts.description ? opts.description : undefined;
  const id = opts.dedupeKey; // sonner deduplicates by id automatically

  if (opts.variant === "destructive") {
    sonnerToast.error(message, { id, description: body, duration: opts.duration });
  } else if (opts.variant === "success") {
    sonnerToast.success(message, { id, description: body, duration: opts.duration });
  } else if (opts.variant === "warning") {
    sonnerToast.warning(message, { id, description: body, duration: opts.duration });
  } else {
    sonnerToast(message, { id, description: body, duration: opts.duration });
  }
}

/** @deprecated Use `toast` directly instead. `toasts` array is not available with sonner. */
function useToast() {
  return { toast, toasts: [] as never[], dismiss: sonnerToast.dismiss };
}

export { useToast, toast };
